/**
 * -----------------------------------------------------------------------------
 * Popup - Create New Document
 * -----------------------------------------------------------------------------
 * This popup is used for creating a new Document in the organization.
 */

import { Formik, useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useUploadOrganisationDocumentMutation } from "../../../api/network/adminApiServices";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import { TModalsState, setModalsData } from "../../../api/store/commonSlice";
import { AdminFormFieldInput, AdminFormFieldSubmit } from "../../../components/admin/formFields";
import { formInitialValues, TFormFieldNames, YupValidationSchema } from "./validation";
import FTModal from "../../../components/common/FTModal";
import { AdminFormFileInput } from "../../../components/admin/fileFields";
import { useEffect, useMemo, useState } from "react";
import FormActionButtons from "../../../components/admin/formActionButtons";

const AdminsDocumentCreateNew = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation('translation', { keyPrefix: 'admins.documents.create_new' });
  const modalsState = useSelector((state: { commonReducer: { modals: TModalsState } }) => state.commonReducer.modals);
  const dispatch = useDispatch();

  // fleettag mutation
  const [uploadOrgDocumentAPITrigger] = useUploadOrganisationDocumentMutation();

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showUploadDocumentModal: false }));
  };

  const [fileName, setFileName] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      setFileName(null);
    };
  }, []);
      const formik = useFormik({
        initialValues:formInitialValues,
        validationSchema:YupValidationSchema,
        onSubmit:(values, { setSubmitting }) => {
          if (!values.file) {
            return;
          }
          const formData = new FormData();
          formData.append('name', values.name);
          formData.append('file', values?.file as File);
          uploadOrgDocumentAPITrigger(formData)
            .unwrap()
            .then((data) => {
              if (!!data?.id) {
                dispatch(setModalsData({ ...modalsState, showUploadDocumentModal: false }));
                toast.success(t('toast.create_success'), { autoClose: 10000 });
              } else {
                toast.error(t('toast.create_failed'));
              }
            })
            .catch((error) => {
              const errors = !!error?.data ? serializeErrorKeyValues(error?.data) : [t('toast.create_failed')];
              toast.error(errors?.join(' '));
            })
            .finally(() => {
              setSubmitting(false);
            });
        }
      });
      const{
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        dirty,
        setFieldValue,
      } = formik;
        const invalidFields =
          Object.keys(errors)
            .filter(key => !!errors[key as TFormFieldNames]);
        const isFormValid = invalidFields.length === 0 && dirty;
        const MemoizedFormActionButtons = useMemo(() => {
          return (
            <>
              <FormActionButtons
                buttons={[
                  {
                    label:t('submit_button'),
                    variant:"primary",
                    disabled:!isFormValid || isSubmitting /* || isLoading*/
                  },
                  {
                    label:tMain('cancel'),
                    type:"button",
                    variant:"danger-transparent",
                    disabled:isSubmitting , /* || isLoading*/
                    onClick:()=>hideModal()
                  },
                ]}
              />
            </>
          );
        }, [
          isFormValid,
          isSubmitting,    
        ])

  return (
    <FTModal
      visible={modalsState.showUploadDocumentModal}
      heading={t("heading")}
      onHide={hideModal}
    >
          <form className="grid grid-cols-6 gap-6" onSubmit={handleSubmit}>
            <p className="col-span-6 mt-0 leading-relaxed text-gray-500">
              {t("sub_heading")}
            </p>

            <AdminFormFieldInput
              label={t('name')}
              type="text"
              id="Name"
              name="name"
              placeholder={t('name_placeholder')}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              touched={touched.name}
              error={errors.name}
            />

            <AdminFormFileInput
              label={t('file')}
              id="File"
              onChange={(file) => {
                setFileName(file?.name || null);
                setFieldValue("file", file)
              }}
              fileName={fileName}
              value={values.file}
              error={errors.file}
            />
            {MemoizedFormActionButtons}
            {/* <AdminFormFieldSubmit
              label={t('submit_button')}
              variant="primary"
              disabled={!isFormValid || isSubmitting } // || isLoading
            />

            <AdminFormFieldSubmit
              label={tMain('cancel')}
              type="button"
              variant="danger-transparent"
              disabled={isSubmitting } // || isLoading
              onClick={hideModal}
            /> */}
          </form>
    </FTModal>
  )
};
export default AdminsDocumentCreateNew;